<template lang="pug">
  div.yz-address-input
    yz-text-field.mb-2(:value="line1" @input="updateLine1" :placeholder="$t('line1_placeholder')" :required="required || !checkEmptyAddress" :required-error-message="$t('line1_required')" :readonly="readonly" :disabled="disabled" :size="size")

    yz-text-field.mb-2(:value="line2" @input="updateLine2" :placeholder="$t('line2_placeholder')" :readonly="readonly" :disabled="disabled" :size="size")

    yz-text-field.mb-2(:value="postcode" @input="updatePostcode" :placeholder="$t('postcode_placeholder')" :required="required || !checkEmptyAddress" :required-error-message="$t('postcode_required')" :rules="!checkEmptyAddress ? postcodeRules : []" :readonly="readonly" :disabled="disabled" :size="size")

    yz-text-field.mb-2(:value="city" @input="updateCity" :placeholder="$t('city_placeholder')" :required="required || !checkEmptyAddress" :required-error-message="$t('city_required')" :loading="isFetchingAddress" :readonly="readonly" :disabled="disabled" :size="size")

    // State field - dropdown for Malaysia, text field for general
    yz-dropdown.mb-2(v-if="type === 'malaysia'" :value="state" @input="updateState" :placeholder="$t('state_placeholder')" clearable :items="stateOptionsFormatted" :required="required || !checkEmptyAddress" :required-error-message="$t('state_required')" :loading="isFetchingAddress" :readonly="readonly" :disabled="disabled" :size="size")
    yz-text-field.mb-2(v-else :value="state" @input="updateState" :placeholder="$t('state_placeholder')" :required="required || !checkEmptyAddress" :required-error-message="$t('state_required')" :readonly="readonly" :disabled="disabled" :size="size")

    yz-text-field(:value="countryValue" @input="updateCountry" :placeholder="$t('country_placeholder')" :required="required || !checkEmptyAddress" :required-error-message="$t('country_required')" :readonly="readonly || (type === 'malaysia')" :disabled="disabled" :size="size")

</template>

<script>
import {mapGetters} from 'vuex'
import {debounce} from "@/utils"

export default {
  name: 'YzAddressInput',
  props: {
    // Address field values
    line1: {
      type: String,
      default: ''
    },
    line2: {
      type: String,
      default: ''
    },
    postcode: {
      type: String,
      default: ''
    },
    city: {
      type: String,
      default: ''
    },
    state: {
      type: String,
      default: ''
    },
    country: {
      type: String,
      default: ''
    },

    // Control props
    required: {
      type: Boolean,
      default: false
    },
    readonly: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    },

    // Layout props
    size: {
      type: String,
      default: 'lg',
      validator: (value) => ['lg', 'md', 'sm'].includes(value)
    },

    // Address type
    type: {
      type: String,
      default: 'general',
      validator: (value) => ['malaysia', 'general'].includes(value)
    },

  },
  data() {
    return {
      isFetchingAddress: false
    }
  },
  computed: {
    ...mapGetters({
      stateOptions: 'getStates'
    }),

    stateOptionsFormatted() {
      return this.stateOptions.map(option => ({text: option, value: option}))
    },

    // Conditional postcode validation rules
    postcodeRules() {
      if (this.type === 'malaysia') {
        return [
          v => !v || /^[0-9]{5}$/.test(v) || 'Postcode must be exactly 5 digits'
        ]
      }
      return [] // No validation rules for general type
    },

    // Country value - auto-set to Malaysia for malaysia type
    countryValue() {
      if (this.type === 'malaysia') {
        return 'Malaysia'
      }
      return this.country
    },

    checkEmptyAddress() {
      const address = {
        address_1: this.line1,
        address_2: this.line2,
        city: this.city,
        postcode: this.postcode,
        state: this.state
      }

      // Only include country in empty check if not malaysia type or if required
      if (this.type !== 'malaysia' || this.required) {
        address.country = this.country
      }

      if (Object.keys(address).length === 0) {
        return true
      } else if (Object.values(address).every(val => !val)) {
        return true
      }
      return false
    },

    hasError() {
      // Simple check if any required field is missing when required is true
      if (this.required && !this.checkEmptyAddress) {
        return !this.line1 || !this.postcode || !this.city || !this.state || !this.country;
      }
      return false;
    }
  },
  watch: {
    // Watch for type changes to auto-set country for Malaysia
    type: {
      handler(newType) {
        if (newType === 'malaysia') {
          this.$emit('update:country', 'Malaysia')
        }
      },
      immediate: true
    }
  },
  mounted() {
    // Register with parent v-form if it exists (following YDS pattern)
    this.form = this.getForm();
    if (this.form && this.form.register) {
      this.form.register(this);
    }
  },
  beforeDestroy() {
    // Unregister from parent v-form (following YDS pattern)
    if (this.form && this.form.unregister) {
      this.form.unregister(this);
    }
  },
  methods: {
    // Update methods with .sync support
    updateLine1(value) {
      this.$emit('update:line1', value)
    },
    updateLine2(value) {
      this.$emit('update:line2', value)
    },
    updatePostcode(value) {
      this.$emit('update:postcode', value)
      this.fetchPostcodes(value)
    },
    updateCity(value) {
      this.$emit('update:city', value)
    },
    updateState(value) {
      this.$emit('update:state', value)
    },
    updateCountry(value) {
      // For Malaysia type, always emit 'Malaysia' regardless of input
      if (this.type === 'malaysia') {
        this.$emit('update:country', 'Malaysia')
      } else {
        this.$emit('update:country', value)
      }
    },

    // Postcode auto-fetch functionality
    fetchPostcodes: debounce(function (val) {
      if (!val) {
        return
      }
      if (val.length !== 5) {
        return
      }
      this.isFetchingAddress = true
      this.$axios.get(`/shipping/postcodes?postcode=${val}`).then((response) => {
        const addresses = response.data
        if (addresses.length > 0 && addresses[0].postcode) {
          this.updateCity(addresses[0].city)
          this.updateState(addresses[0].state)
          this.updateCountry('Malaysia')
        }
      })
          .catch(() => {
            // Handle error silently like original implementation
          })
          .finally(() => {
            this.isFetchingAddress = false
          })
    }, 300),

    // Main validation method that v-form will call (simplified for v-form integration)
    validate() {
      // Simple validation: check if required fields are filled when required is true
      if (this.required && !this.checkEmptyAddress) {
        return !!(this.line1 && this.postcode && this.city && this.state && this.country);
      }
      return true;
    },

    // Helper method to find parent v-form (following YDS pattern)
    getForm() {
      let parent = this.$parent;

      while (parent) {
        // Check if it's a v-form by name or by checking for form methods
        if (parent.$options.name === 'v-form' || (parent.register && parent.unregister && parent.validate)) {
          return parent;
        }

        // Also check if parent has a form ref (for custom form wrappers)
        if (parent.$refs && parent.$refs.form &&
            parent.$refs.form.register && parent.$refs.form.unregister) {
          return parent.$refs.form;
        }

        parent = parent.$parent;
      }

      return null;
    }
  }
}
</script>

<style lang="scss" scoped>
</style>

<i18n>
{
  "en": {
    "line1_placeholder": "Enter address line 1",
    "line1_required": "Address line 1 is required",
    "line2_placeholder": "Enter address line 2",
    "postcode_placeholder": "Enter postcode",
    "postcode_required": "Postcode is required",
    "city_placeholder": "Enter city",
    "city_required": "City is required",
    "state_placeholder": "Select state",
    "state_required": "Please select state",
    "country_placeholder": "Enter country",
    "country_required": "Country is required"
  },
  "id": {
    "line1_placeholder": "Masukkan baris alamat 1",
    "line1_required": "Baris alamat 1 diperlukan",
    "line2_placeholder": "Masukkan baris alamat 2",
    "postcode_placeholder": "Masukkan kode pos",
    "postcode_required": "Kode pos diperlukan",
    "city_placeholder": "Masukkan kota",
    "city_required": "Kota diperlukan",
    "state_placeholder": "Pilih negeri",
    "state_required": "Silakan pilih negeri",
    "country_placeholder": "Masukkan negara",
    "country_required": "Negara diperlukan"
  }
}
</i18n>